/**
 * Sharing roles, mirroring EXPERIENCE_ROLES in
 * supabase/functions/_permissions/capabilities.ts. Alignment is checked by
 * `yarn check:vocabularies`.
 *
 * The lookup table `experience_roles` is not readable by clients (RLS enabled,
 * zero policies): it exists for referential integrity, so these ids are the
 * contract and this file is where the UI gets them.
 */
export const EXPERIENCE_ROLES = {
  OWNER: 1,
  EDITOR: 2,
  LISTENER: 3,
  CONTRIBUTOR: 4,
  SUGGESTER: 5,
} as const

export type ExperienceRole = (typeof EXPERIENCE_ROLES)[keyof typeof EXPERIENCE_ROLES]

/**
 * Slugs for the i18n keys: payloads carry the number, translations speak by name.
 * A translator reads `listener`, not `3`.
 */
export const ROLE_SLUGS: Record<number, string> = {
  [EXPERIENCE_ROLES.OWNER]: 'owner',
  [EXPERIENCE_ROLES.EDITOR]: 'editor',
  [EXPERIENCE_ROLES.LISTENER]: 'listener',
  [EXPERIENCE_ROLES.CONTRIBUTOR]: 'contributor',
  [EXPERIENCE_ROLES.SUGGESTER]: 'suggester',
}

/**
 * What the owner may assign. Twin of DELEGABLE_ROLES in the edge functions, so the
 * dropdown cannot offer a role the backend refuses with a 400.
 *
 * `owner` is not delegated but transferred; `editor` has no real power yet and
 * `contributor` is deferred — both are therefore kept out of the dropdown while
 * remaining valid for existing shares.
 */
export const DELEGABLE_ROLES: readonly number[] = [EXPERIENCE_ROLES.LISTENER, EXPERIENCE_ROLES.SUGGESTER]

/** i18n key for a role's label, hint or unavailability note. */
export const roleKey = (role: number, field: 'label' | 'hint' | 'unavailable'): string =>
  `sharing.roles.${ROLE_SLUGS[role] ?? 'unknown'}.${field}`
